import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Send, Save, Calendar, Paperclip, Check, MessageSquare, BarChart2, Trash2, Megaphone, FileText, Eye, MousePointer, AlertTriangle, Ban, Upload, Target, Folder, DoorOpen, Mail, Smartphone, Pencil, User, Users, Building2, Cake, MapPin, Square, Tag, Info, Bell } from 'lucide-react';
import { useConfirm } from '../components/ConfirmModal';

const API = 'https://qbdp-backend-production.up.railway.app';

const TEMPLATES = [
  { id: 'soiree', Icon: Sparkles,  label: 'Soirée',      color: '#6c3483', accent: '#9b59b6' },
  { id: 'promo',  Icon: Tag,       label: 'Promotion',   color: '#b7950b', accent: '#f39c12' },
  { id: 'event',  Icon: Calendar,  label: 'Événement',   color: '#1a5276', accent: '#2980b9' },
  { id: 'info',   Icon: Info,      label: 'Information', color: '#1e8449', accent: '#27ae60' },
  { id: 'default',Icon: Megaphone, label: 'Général',     color: '#1a1a2e', accent: '#1a56db' },
];

const TYPES = [
  { id: 'email', label: 'Email',       Icon: Mail,        Icon2: null },
  { id: 'sms',   label: 'SMS',         Icon: Smartphone,  Icon2: null },
  { id: 'both',  label: 'Email + SMS', Icon: Mail,        Icon2: Smartphone },
];

const STATUTS_CLIENT = [
  { id: '',         label: 'Tous statuts' },
  { id: 'reserve',  label: 'Réservé' },
  { id: 'actif',    label: 'Présent' },
  { id: 'checkout', label: 'Absent' },
];

const MAX_FILE_MB = 8;

function badge(status) {
  const map = {
    draft:    { label: 'Brouillon', bg: '#f3f4f6', color: '#6b7280' },
    scheduled:{ label: 'Planifiée', bg: '#fffbeb', color: '#d97706' },
    sending:  { label: 'En cours',  bg: '#eff6ff', color: '#2563eb' },
    sent:     { label: 'Envoyée',   bg: '#f0fdf4', color: '#16a34a' },
  };
  const s = map[status] || map.draft;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: s.bg, color: s.color }}>{s.label}</span>;
}

const S = {
  label:   { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' },
  input:   { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  textarea:{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 110, boxSizing: 'border-box', fontFamily: 'inherit' },
  select:  { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' },
  btn: (primary, color) => ({
    padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
    background: primary ? (color || '#1a56db') : '#f3f4f6',
    color: primary ? '#fff' : '#374151',
    display: 'flex', alignItems: 'center', gap: 6,
  }),
  chip: (sel, color) => ({
    padding: '8px 16px', borderRadius: 10,
    border: `2px solid ${sel ? (color || '#1a56db') : '#e5e7eb'}`,
    background: sel ? (color ? color + '15' : '#eff6ff') : '#fff',
    color: sel ? (color || '#1a56db') : '#374151',
    cursor: 'pointer', fontSize: 13, fontWeight: sel ? 600 : 400,
    display: 'flex', alignItems: 'center', gap: 6,
  }),
  stepDot: (active, done) => ({
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
    background: done ? '#16a34a' : active ? '#1a56db' : '#f3f4f6',
    color: done || active ? '#fff' : '#9ca3af',
  }),
};

function Preview({ form }) {
  const tpl = TEMPLATES.find(t => t.id === form.template) || TEMPLATES[4];
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', fontSize: 13 }}>
      <div style={{ background: `linear-gradient(135deg, ${tpl.color}, ${tpl.accent})`, padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ display:'flex', justifyContent:'center' }}><tpl.Icon size={28} color="rgba(255,255,255,0.9)" /></div>
        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>{tpl.label}</div>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <div style={{ color: '#6b7280', marginBottom: 6 }}>Bonjour <strong>Prénom</strong> 👋</div>
        <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{form.subject || tpl.label}</div>
        <div style={{ color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 12 }}>{form.content || 'Votre message apparaîtra ici...'}</div>
        {form.attachment_name && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Paperclip size={13} color="#6b7280" /><span style={{ fontSize: 12 }}>{form.attachment_name}</span>
          </div>
        )}
      </div>
      <div style={{ background: '#f9fafb', padding: '10px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'center', color: '#9ca3af', fontSize: 11 }}>
        qbdp • Se désabonner
      </div>
    </div>
  );
}

// Formule de Haversine — distance en km entre deux points GPS
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon/2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function SelectionDestinataires({ selected, onChange, showToast, preselectAnniv }) {
  const [tab, setTab]           = useState('employes');
  const [employes, setEmployes] = useState([]);
  const [clients, setClients]   = useState([]);
  const [services, setServices] = useState([]);
  const [loadingE, setLoadingE] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [filtreService, setFiltreService]       = useState('');
  const [filtreStatut, setFiltreStatut]         = useState('');
  const [filtrePresenceDu, setFiltrePresenceDu] = useState('');
  const [filtrePresenceAu, setFiltrePresenceAu] = useState('');
  const [filtreAnniv, setFiltreAnniv]           = useState(preselectAnniv || '');
  const [filtreRayon, setFiltreRayon]           = useState('');
  const [etablCoords, setEtablCoords]           = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    fetch(`${API}/api/employes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setEmployes(Array.isArray(d) ? d : []); setLoadingE(false); }).catch(() => setLoadingE(false));
    fetch(`${API}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setClients(Array.isArray(d) ? d : []); setLoadingC(false); }).catch(() => setLoadingC(false));
    fetch(`${API}/api/services`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setServices(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/api/personnalisation/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.adm_latitude && d.adm_longitude) setEtablCoords({ lat: d.adm_latitude, lng: d.adm_longitude }); })
      .catch(() => {});
    if (preselectAnniv) setTab('clients');
  }, []);

  const today = new Date();

  const clientsFiltres = clients.filter(c => {
    if (filtreStatut && c.statut !== filtreStatut) return false;
    if (filtrePresenceDu && (!c.date_depart || new Date(c.date_depart) < new Date(filtrePresenceDu))) return false;
    if (filtrePresenceAu && (!c.date_arrivee || new Date(c.date_arrivee) > new Date(filtrePresenceAu))) return false;
    if (filtreAnniv && c.date_naissance) {
      const m = new Date(c.date_naissance).getMonth() + 1;
      const d = new Date(c.date_naissance).getDate();
      if (filtreAnniv === 'today') {
        if (m !== today.getMonth()+1 || d !== today.getDate()) return false;
      } else if (filtreAnniv === 'week') {
        const start = new Date(today); start.setDate(today.getDate() - today.getDay() + 1);
        const end   = new Date(start); end.setDate(start.getDate() + 6);
        const anniv = new Date(today.getFullYear(), m-1, d);
        if (anniv < start || anniv > end) return false;
      } else if (filtreAnniv === 'month') {
        if (m !== today.getMonth()+1) return false;
      }
    } else if (filtreAnniv) { return false; }
    if (filtreRayon && etablCoords && c.latitude && c.longitude) {
      const dist = haversine(etablCoords.lat, etablCoords.lng, c.latitude, c.longitude);
      if (dist > parseInt(filtreRayon)) return false;
    }
    return true;
  });

  // Calcul distance pour affichage
  const getDistance = (c) => {
    if (!etablCoords || !c.latitude || !c.longitude) return null;
    return Math.round(haversine(etablCoords.lat, etablCoords.lng, c.latitude, c.longitude));
  };

  const employesFiltres = employes.filter(e => !filtreService || e.service === filtreService);
  const currentList = tab === 'employes' ? employesFiltres : clientsFiltres;
  const currentType = tab === 'employes' ? 'employe' : 'client';
  const isSelected  = (item) => selected.some(s => s.id === item.id && s.type === currentType);

  const toggle = (item) => {
    if (isSelected(item)) onChange(selected.filter(s => !(s.id === item.id && s.type === currentType)));
    else onChange([...selected, { id: item.id, nom: item.nom, email: item.email, telephone: item.telephone, type: currentType }]);
  };

  const toggleAll = () => {
    const allSel = currentList.every(item => isSelected(item));
    if (allSel) {
      onChange(selected.filter(s => s.type !== currentType || !currentList.find(i => i.id === s.id)));
    } else {
      const toAdd = currentList.filter(item => !isSelected(item))
        .map(item => ({ id: item.id, nom: item.nom, email: item.email, telephone: item.telephone, type: currentType }));
      onChange([...selected, ...toAdd]);
    }
  };

  const countE = selected.filter(s => s.type === 'employe').length;
  const countC = selected.filter(s => s.type === 'client').length;
  const allCurrentSelected = currentList.length > 0 && currentList.every(item => isSelected(item));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, background: '#eff6ff', borderRadius: 10, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#1a56db', fontWeight: 600, display:'flex', alignItems:'center', gap:5 }}>
            <Users size={14} /> {selected.length} destinataire{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#6b7280' }}>
            {countE > 0 && <span style={{ display:'flex', alignItems:'center', gap:3 }}><User size={11} /> {countE} employé{countE > 1 ? 's' : ''}</span>}
            {countC > 0 && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Building2 size={11} /> {countC} client{countC > 1 ? 's' : ''}</span>}
          </div>
        </div>
        {selected.length > 0 && (
          <button onClick={() => onChange([])} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #fee2e2', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            Tout effacer
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 14, background: '#f3f4f6', borderRadius: 10, padding: 4 }}>
        {[{ id: 'employes', Icon: User,      label: `Employés${countE > 0 ? ` (${countE})` : ''}` },
          { id: 'clients',  Icon: Building2, label: `Clients${countC > 0 ? ` (${countC})` : ''}` }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400, background: tab === t.id ? '#fff' : 'transparent',
              color: tab === t.id ? '#1a1a2e' : '#6b7280', boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'employes' && (
        <div style={{ marginBottom: 12 }}>
          <select style={S.select} value={filtreService} onChange={e => setFiltreService(e.target.value)}>
            <option value="">Tous les services</option>
            {services.map(s => <option key={s.id} value={s.nom}>{s.nom}</option>)}
          </select>
        </div>
      )}
      {tab === 'clients' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
          <select style={S.select} value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
            {STATUTS_CLIENT.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Présents à partir du</div>
            <input type="date" style={S.input} value={filtrePresenceDu} onChange={e => setFiltrePresenceDu(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>Présents jusqu'au</div>
            <input type="date" style={S.input} value={filtrePresenceAu} onChange={e => setFiltrePresenceAu(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, display:'flex', alignItems:'center', gap:3 }}><Cake size={11} /> Anniversaire</div>
            <select style={S.select} value={filtreAnniv} onChange={e => setFiltreAnniv(e.target.value)}>
              <option value="">Tous</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois-ci</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, display:'flex', alignItems:'center', gap:3 }}><MapPin size={11} /> Rayon</div>
            <select style={S.select} value={filtreRayon} onChange={e => setFiltreRayon(e.target.value)}
              disabled={!etablCoords} title={!etablCoords ? "Enregistrez l'adresse de l'établissement dans Personnalisation" : ''}>
              <option value="">Tous</option>
              <option value="50">≤ 50 km</option>
              <option value="100">≤ 100 km</option>
              <option value="200">≤ 200 km</option>
              <option value="500">≤ 500 km</option>
            </select>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{currentList.length} personne{currentList.length > 1 ? 's' : ''} trouvée{currentList.length > 1 ? 's' : ''}</span>
        {currentList.length > 0 && (
        <button onClick={toggleAll} style={{
            fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 8, padding: '6px 12px',
            background: allCurrentSelected ? '#fef2f2' : '#eff6ff',
            color: allCurrentSelected ? '#dc2626' : '#1a56db',
            border: `1px solid ${allCurrentSelected ? '#fecaca' : '#bfdbfe'}`
          }}>
            {allCurrentSelected ? '☑ Tout désélectionner' : '☑ Tout sélectionner'}
          </button>
        )}
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        {(tab === 'employes' ? loadingE : loadingC) ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Chargement...</div>
        ) : currentList.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Aucune personne trouvée</div>
        ) : currentList.map((item, i) => {
          const sel = isSelected(item);
          return (
            <div key={item.id} onClick={() => toggle(item)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
                background: sel ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#fafafa',
                borderBottom: i < currentList.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${sel ? '#1a56db' : '#d1d5db'}`,
                background: sel ? '#1a56db' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {sel && <Check size={11} color="#fff" />}
              </div>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: sel ? '#1a56db' : '#e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                color: sel ? '#fff' : '#6b7280', flexShrink: 0 }}>
                {item.nom?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {item.nom}
                  {item.date_naissance && (() => {
                    const dn = new Date(item.date_naissance);
                    const isToday = dn.getMonth()+1 === today.getMonth()+1 && dn.getDate() === today.getDate();
                    return isToday ? <span style={{ fontSize: 14 }}>🎂</span> : null;
                  })()}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', gap: 10 }}>
                  {tab === 'employes' && item.service && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Folder size={11} />{item.service}</span>}
                  {tab === 'clients' && item.date_naissance && <span>🎂 {new Date(item.date_naissance).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}</span>}
                  {tab === 'clients' && item.date_arrivee && <span>📅 {new Date(item.date_arrivee).toLocaleDateString('fr-FR')} → {item.date_depart ? new Date(item.date_depart).toLocaleDateString('fr-FR') : '?'}</span>}
                  {tab === 'clients' && item.numero_chambre && <span style={{ display:'flex', alignItems:'center', gap:3 }}><DoorOpen size={11} />Ch. {item.numero_chambre}</span>}
                  {tab === 'clients' && (() => { const dist = getDistance(item); return dist !== null ? <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '1px 6px', borderRadius: 6, fontWeight: 600 }}>📍 {dist} km</span> : null; })()}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af' }}>
                {item.email && <div style={{ display:'flex', alignItems:'center', gap:3 }}><Send size={10} />{item.email}</div>}
                {item.telephone && <div>{item.telephone}</div>}
                {!item.email && !item.telephone && <div style={{ color: '#fbbf24', display:'flex', alignItems:'center', gap:3 }}><AlertTriangle size={11} />Pas de contact</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── WIZARD NOUVELLE CAMPAGNE (2 étapes) ───────────────────────────────────────
function WizardNouvelle({ onClose, onSaved, showToast }) {
  const [step, setStep]       = useState(1);
  const [saving, setSaving]   = useState(false);
  const fileInputRef          = useRef(null);
  const token = sessionStorage.getItem('qbdp_token');
  const [form, setForm] = useState({
    name: '', type: 'email',
    subject: '', content: '', template: 'default',
    attachment_name: '', attachment_data: '', attachment_type: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { showToast(`Max ${MAX_FILE_MB} Mo`, false); return; }
    const allowed = ['application/pdf','image/jpeg','image/png','image/gif','image/webp'];
    if (!allowed.includes(file.type)) { showToast('Format non supporté', false); return; }
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, attachment_name: file.name, attachment_data: ev.target.result.split(',')[1], attachment_type: file.type }));
    reader.readAsDataURL(file);
  };

  const handleSaveDraft = async () => {
    if (!form.name) { showToast('Donnez un nom à la campagne', false); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name, type: form.type, subject: form.subject,
        content: form.content || ' ', template: form.template,
        cible: 'clients', status: 'draft',
        attachment_name: form.attachment_name || null,
        attachment_data: form.attachment_data || null,
        attachment_type: form.attachment_type || null,
      };
      const r = await fetch(`${API}/api/campaigns`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); setSaving(false); return; }
      showToast('Brouillon enregistré ✓');
      onSaved(); onClose();
    } catch { showToast('Erreur réseau', false); }
    setSaving(false);
  };

  const next = () => {
    if (!form.name) { showToast('Donnez un nom à la campagne', false); return; }
    setStep(2);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: 820, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={17} color="#1a56db" /> Nouvelle campagne</div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Étape {step} sur 2</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}><X size={20} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            {[1, 2].map((n, i) => (
              <React.Fragment key={n}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={S.stepDot(step === n, step > n)}>{step > n ? <Check size={12} /> : n}</div>
                  <span style={{ fontSize: 12, color: step===n?'#1a56db':step>n?'#16a34a':'#9ca3af', fontWeight: step===n?600:400 }}>
                    {['Nom & Type', 'Contenu'][i]}
                  </span>
                </div>
                {i < 1 && <div style={{ flex: 1, height: 2, background: step > n ? '#16a34a' : '#e5e7eb', borderRadius: 2 }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>
          {step === 1 && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={S.label}>Nom de la campagne</label>
                <input style={S.input} placeholder="Ex: Soirée d'été 2025" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
              </div>
              <div>
                <label style={S.label}>Type d'envoi</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {TYPES.map(t => (
                    <div key={t.id} style={S.chip(form.type === t.id)} onClick={() => set('type', t.id)}>
                      <t.Icon size={14} />{t.Icon2 && <t.Icon2 size={14} />} {t.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <div style={{ marginBottom: 14 }}>
                  <label style={S.label}>Template visuel</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {TEMPLATES.map(t => (
                      <div key={t.id} onClick={() => set('template', t.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10,
                          border: `2px solid ${form.template===t.id?t.accent:'#e5e7eb'}`,
                          background: form.template===t.id?t.accent+'12':'#fff', cursor: 'pointer' }}>
                        <t.Icon size={16} color={form.template===t.id ? t.accent : '#6b7280'} />
                        <span style={{ fontSize: 13, fontWeight: form.template===t.id?600:400 }}>{t.label}</span>
                        {form.template===t.id && <Check size={12} color={t.accent} style={{ marginLeft:'auto' }} />}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={S.label}>Objet / Titre</label>
                  <input style={S.input} placeholder="Objet de l'email" value={form.subject} onChange={e => set('subject', e.target.value)} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={S.label}>Message</label>
                  <textarea style={S.textarea} placeholder="Votre message..." value={form.content} onChange={e => set('content', e.target.value)} />
                </div>
                <div>
                  <label style={S.label}><Paperclip size={12} style={{ display:'inline', marginRight:4 }} />Pièce jointe (PDF, JPG, PNG — max {MAX_FILE_MB} Mo)</label>
                  {form.attachment_name ? (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10 }}>
                      <Paperclip size={14} color="#15803d" />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#15803d' }}>{form.attachment_name}</div>
                        <div style={{ fontSize:11, color:'#6b7280' }}>Prêt à envoyer</div>
                      </div>
                      <button onClick={() => { setForm(f=>({...f,attachment_name:'',attachment_data:'',attachment_type:''})); if(fileInputRef.current) fileInputRef.current.value=''; }}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center' }}><X size={16} /></button>
                    </div>
                  ) : (
                    <div onClick={() => fileInputRef.current?.click()}
                      style={{ border:'2px dashed #e5e7eb', borderRadius:10, padding:'18px', textAlign:'center', cursor:'pointer' }}
                      onMouseEnter={e=>e.currentTarget.style.borderColor='#1a56db'}
                      onMouseLeave={e=>e.currentTarget.style.borderColor='#e5e7eb'}>
                      <div style={{ display:'flex', justifyContent:'center', marginBottom:4 }}><Paperclip size={22} color="#9ca3af" /></div>
                      <div style={{ fontSize:13, color:'#374151', fontWeight:500 }}>Cliquer pour ajouter un flyer</div>
                      <div style={{ fontSize:11, color:'#9ca3af', marginTop:3 }}>PDF, JPG ou PNG • max {MAX_FILE_MB} Mo</div>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" style={{ display:'none' }} onChange={handleFileChange} />
                </div>
              </div>
              <div>
                <label style={S.label}>Aperçu</label>
                <Preview form={form} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', gap:10 }}>
          <div style={{ display:'flex', gap:8 }}>
            {step > 1 && <button onClick={() => setStep(1)} style={S.btn(false)}>← Retour</button>}
            <button onClick={onClose} style={S.btn(false)}>Annuler</button>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {step === 1 && <button onClick={next} style={S.btn(true)}>Suivant →</button>}
            {step === 2 && (
              <button onClick={handleSaveDraft} style={S.btn(true, '#374151')} disabled={saving}>
                {saving ? 'Sauvegarde...' : <><Save size={14} /> Enregistrer comme brouillon</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WIZARD MODIFIER CAMPAGNE (3 onglets) ──────────────────────────────────────
function WizardModifier({ campaign, defaultTab, onClose, onSaved, showToast }) {
  const token = sessionStorage.getItem('qbdp_token');
  const fileInputRef = useRef(null);
  const [saving, setSaving]         = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [tab, setTab]               = useState(defaultTab || 'contenu');

  // toLocalDT: convert ISO string → datetime-local format YYYY-MM-DDTHH:MM
  const toLocalDT = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [form, setForm] = useState({
    name:            campaign.name            || '',
    subject:         campaign.subject         || '',
    content:         campaign.content         || '',
    template:        campaign.template        || 'default',
    cible:           campaign.cible           || 'clients',
    scheduled_at:    toLocalDT(campaign.scheduled_at),
    attachment_name: campaign.attachment_name || '',
    attachment_data: '',
    attachment_type: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Destinataires sélectionnés manuellement
  const [selected, setSelected] = useState(
    Array.isArray(campaign.recipient_ids) ? campaign.recipient_ids : []
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { showToast(`Max ${MAX_FILE_MB} Mo`, false); return; }
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, attachment_name: file.name, attachment_data: ev.target.result.split(',')[1], attachment_type: file.type }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name) { showToast('Donnez un nom', false); return; }
    if (tab === 'destinataires' && selected.length === 0) {
      showToast('Sélectionnez au moins un destinataire', false); return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name, subject: form.subject, content: form.content,
        template: form.template, cible: form.cible,
        attachment_name: form.attachment_name || null,
        recipient_ids: selected.map(s => ({ id: s.id, type: s.type })),
      };
      if (form.attachment_data) { body.attachment_data = form.attachment_data; body.attachment_type = form.attachment_type; }
      const r = await fetch(`${API}/api/campaigns/${campaign.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); setSaving(false); return; }
      showToast('Campagne modifiée ✓');
      onSaved();
    } catch { showToast('Erreur réseau', false); }
    setSaving(false);
  };

  // Save scheduled_at then activate scheduling
  const handleActivatePlanification = async () => {
    if (!form.scheduled_at) { showToast('Choisissez une date et une heure', false); return; }
    setScheduling(true);
    try {
      // 1) Save scheduled_at as UTC ISO
      const putBody = {
        name: form.name, subject: form.subject, content: form.content,
        template: form.template, cible: form.cible,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        attachment_name: form.attachment_name || null,
        recipient_ids: selected.map(s => ({ id: s.id, type: s.type })),
      };
      const r1 = await fetch(`${API}/api/campaigns/${campaign.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(putBody),
      });
      if (!r1.ok) { const e = await r1.json(); showToast(e.error || 'Erreur', false); setScheduling(false); return; }

      // 2) Activate schedule
      const r2 = await fetch(`${API}/api/campaigns/${campaign.id}/schedule`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d2 = await r2.json();
      if (!r2.ok) { showToast(d2.error || 'Erreur planification', false); setScheduling(false); return; }
      showToast('Campagne planifiée ✓');
      onSaved();
    } catch { showToast('Erreur réseau', false); }
    setScheduling(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:20, width:860, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'24px 28px 16px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:17, color:'#1a1a2e' }}>Modifier — {campaign.name}</div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', display:'flex', alignItems:'center' }}><X size={20} /></button>
          </div>
          {/* Onglets */}
          <div style={{ display:'flex', gap:2, background:'#f3f4f6', borderRadius:10, padding:4 }}>
            {[
              { id:'contenu',       label:'Contenu' },
              { id:'destinataires', label:'Destinataires' },
              { id:'planification', label:'Planification' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ flex:1, padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13,
                  fontWeight: tab===t.id ? 600 : 400, background: tab===t.id ? '#fff' : 'transparent',
                  color: tab===t.id ? '#1a1a2e' : '#6b7280', boxShadow: tab===t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'24px 28px' }}>

          {/* Onglet Contenu */}
          {tab === 'contenu' && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              <div>
                <div style={{ marginBottom:14 }}>
                  <label style={S.label}>Nom</label>
                  <input style={S.input} value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={S.label}>Template visuel</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {TEMPLATES.map(t => (
                      <div key={t.id} onClick={() => set('template', t.id)}
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:10,
                          border:`2px solid ${form.template===t.id?t.accent:'#e5e7eb'}`,
                          background:form.template===t.id?t.accent+'12':'#fff', cursor:'pointer' }}>
                        <span style={{ fontSize:16 }}>{t.emoji}</span>
                        <span style={{ fontSize:13, fontWeight:form.template===t.id?600:400 }}>{t.label}</span>
                        {form.template===t.id && <Check size={12} color={t.accent} style={{ marginLeft:'auto' }} />}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={S.label}>Objet / Titre</label>
                  <input style={S.input} value={form.subject} onChange={e => set('subject', e.target.value)} />
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={S.label}>Message</label>
                  <textarea style={S.textarea} value={form.content} onChange={e => set('content', e.target.value)} />
                </div>
                <div>
                  <label style={S.label}><Paperclip size={12} style={{ display:'inline', marginRight:4 }} />Pièce jointe</label>
                  {form.attachment_name ? (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10 }}>
                      <Paperclip size={14} color="#15803d" />
                      <div style={{ flex:1, fontSize:13, fontWeight:600, color:'#15803d' }}>{form.attachment_name}</div>
                      <button onClick={() => setForm(f => ({ ...f, attachment_name:'', attachment_data:'', attachment_type:'' }))}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', display:'flex', alignItems:'center' }}><X size={16} /></button>
                    </div>
                  ) : (
                    <div onClick={() => fileInputRef.current?.click()}
                      style={{ border:'2px dashed #e5e7eb', borderRadius:10, padding:'14px', textAlign:'center', cursor:'pointer' }}
                      onMouseEnter={e=>e.currentTarget.style.borderColor='#1a56db'}
                      onMouseLeave={e=>e.currentTarget.style.borderColor='#e5e7eb'}>
                      <div style={{ fontSize:13, color:'#374151' }}>Cliquer pour ajouter</div>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFileChange} />
                </div>
              </div>
              <div>
                <label style={S.label}>Aperçu</label>
                <Preview form={form} />
              </div>
            </div>
          )}

          {/* Onglet Destinataires */}
          {tab === 'destinataires' && (
            <div>
              {selected.length > 0 && (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13, color:'#15803d', fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                  <Check size={14} /> {selected.length} destinataire{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''} — cliquez Sauvegarder pour confirmer
                </div>
              )}
              <SelectionDestinataires
                selected={selected}
                onChange={setSelected}
                showToast={showToast}
              />
            </div>
          )}

          {/* Onglet Planification */}
          {tab === 'planification' && (
            <div>
              <div style={{ marginBottom:20 }}>
                <label style={S.label}>Date et heure d'envoi</label>
                <input type="datetime-local" style={S.input} value={form.scheduled_at} onChange={e => set('scheduled_at', e.target.value)} />
                <p style={{ fontSize:12, color:'#9ca3af', marginTop:6 }}>
                  L'heure est en heure locale — elle sera convertie en UTC lors de la sauvegarde.
                </p>
              </div>
              {campaign.status === 'scheduled' && (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'14px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
                  <Check size={18} color="#15803d" />
                  <div>
                    <div style={{ fontWeight:600, fontSize:13, color:'#15803d' }}>Campagne planifiée</div>
                    <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>
                      Envoi prévu le {campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
                    </div>
                  </div>
                </div>
              )}
              <button onClick={handleActivatePlanification} disabled={scheduling || !form.scheduled_at}
                style={{ ...S.btn(true, '#d97706'), opacity: (!form.scheduled_at || scheduling) ? 0.6 : 1 }}>
                {scheduling ? 'Activation...' : <><Calendar size={14} /> Activer la planification</>}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between' }}>
          <button onClick={onClose} style={S.btn(false)}>Fermer</button>
          {tab !== 'planification' && (
            <button onClick={handleSave} style={S.btn(true)} disabled={saving}>
              {saving ? 'Sauvegarde...' : <><Save size={14} /> Sauvegarder</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WIZARD LANCER CAMPAGNE EXISTANTE ──────────────────────────────────────────
function WizardLancer({ onClose, onSaved, showToast, campaigns }) {
  const [sending, setSending]   = useState(false);
  const [campagne, setCampagne] = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  const handleSend = async () => {
    if (!campagne) { showToast('Sélectionnez une campagne', false); return; }
    setSending(true);
    try {
      // Create a copy and send immediately
      const body = {
        name: `${campagne.name} (relance)`,
        type: campagne.type, subject: campagne.subject, content: campagne.content,
        template: campagne.template, attachment_name: campagne.attachment_name,
        cible: campagne.cible || 'clients',
      };
      const r = await fetch(`${API}/api/campaigns`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); setSending(false); return; }
      const r2 = await fetch(`${API}/api/campaigns/${d.id}/send`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d2 = await r2.json();
      if (!r2.ok) showToast(d2.error || 'Erreur envoi', false);
      else showToast(`✅ ${d2.sent} message(s) envoyé(s) !`);
      onSaved(); onClose();
    } catch { showToast('Erreur réseau', false); }
    setSending(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:20, width:700, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding:'24px 28px 16px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontWeight:700, fontSize:17, color:'#1a1a2e', display:'flex', alignItems:'center', gap:8 }}><Send size={17} color="#5b8dd9" /> Lancer une campagne</div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', display:'flex', alignItems:'center' }}><X size={20} /></button>
          </div>
          <p style={{ fontSize:13, color:'#6b7280', marginTop:6 }}>Sélectionnez une campagne existante, une copie sera créée et envoyée immédiatement.</p>
        </div>
        <div style={{ padding:'24px 28px' }}>
          {campaigns.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#9ca3af', border:'1px dashed #e5e7eb', borderRadius:12 }}>
              Aucune campagne — créez-en une d'abord avec ✨ Nouvelle campagne.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {campaigns.map(c => {
                const tplInfo  = TEMPLATES.find(t => t.id === c.template) || TEMPLATES[4];
                const typeInfo = TYPES.find(x => x.id === c.type);
                const sel = campagne?.id === c.id;
                return (
                  <div key={c.id} onClick={() => setCampagne(c)}
                    style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', borderRadius:12,
                      border:`2px solid ${sel?'#1a56db':'#e5e7eb'}`, background:sel?'#eff6ff':'#fff', cursor:'pointer' }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:`linear-gradient(135deg,${tplInfo.color},${tplInfo.accent})`,
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <tplInfo.Icon size={20} color="white" />
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:14, color:'#1a1a2e', marginBottom:3 }}>{c.name}</div>
                      <div style={{ fontSize:12, color:'#6b7280', display:'flex', gap:10 }}>
                        {typeInfo && <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><typeInfo.Icon size={11} />{typeInfo.Icon2 && <typeInfo.Icon2 size={11} />} {typeInfo.label}</span>}
                        {c.subject && <span>• {c.subject}</span>}
                        {c.attachment_name && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Paperclip size={11} />{c.attachment_name}</span>}
                      </div>
                    </div>
                    {sel && <Check size={18} color="#1a56db" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ padding:'16px 28px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', gap:10 }}>
          <button onClick={onClose} style={S.btn(false)}>Annuler</button>
          <button onClick={handleSend} style={S.btn(true, '#16a34a')} disabled={sending || !campagne}>
            {sending ? 'Envoi...' : <><Send size={14} /> Envoyer maintenant</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── WIDGET ANNIVERSAIRES ───────────────────────────────────────────────────────
function WidgetAnniversaires({ onLancer }) {
  const [anniv, setAnniv] = useState(null);
  const token = sessionStorage.getItem('qbdp_token');
  useEffect(() => {
    fetch(`${API}/api/clients/anniversaires`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setAnniv).catch(() => {});
  }, []);
  if (!anniv || !anniv.today || !anniv.week || !anniv.month) return null;
  if (anniv.today.length === 0 && anniv.week.length === 0 && anniv.month.length === 0) return null;
  const cards = [
    { key:'today', label:"Aujourd'hui",  Icon:Cake,        color:'#dc2626', bg:'#fef2f2', border:'#fecaca', list: anniv.today },
    { key:'week',  label:'Cette semaine',Icon:Sparkles,    color:'#d97706', bg:'#fffbeb', border:'#fde68a', list: anniv.week  },
    { key:'month', label:'Ce mois-ci',   Icon:Calendar,    color:'#1a56db', bg:'#eff6ff', border:'#bfdbfe', list: anniv.month },
  ].filter(c => c.list.length > 0);
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}><Cake size={14} color="#374151" /> Rappels anniversaires</div>
      <div style={{ display:'flex', gap:10 }}>
        {cards.map(c => (
          <div key={c.key} onClick={() => onLancer(c.key, c.list)}
            style={{ flex:1, background:c.bg, border:`1px solid ${c.border}`, borderRadius:12, padding:'14px 18px', cursor:'pointer', transition:'transform .15s, box-shadow .15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <c.Icon size={20} color={c.color} />
              <span style={{ fontSize:22, fontWeight:700, color:c.color }}>{c.list.length}</span>
            </div>
            <div style={{ fontSize:13, fontWeight:600, color:c.color }}>{c.label}</div>
            <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>
              {c.list.slice(0,2).map(p => p.nom).join(', ')}{c.list.length > 2 ? '...' : ''}
            </div>
            <div style={{ fontSize:11, color:c.color, marginTop:6, fontWeight:500 }}>Cliquer pour envoyer →</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MODAL STATS EMAIL ─────────────────────────────────────────────────────────
function ModalStats({ campaignId, onClose }) {
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError]       = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  const fetchStats = (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    fetch(`${API}/api/email-stats/${campaignId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setStats(d); setLastUpdate(new Date()); setLoading(false); setRefreshing(false); })
      .catch(() => { setError('Erreur de chargement'); setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(true), 10000);
    return () => clearInterval(interval);
  }, [campaignId]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:20, padding:32, width:500, maxWidth:'90vw', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:'#1a1a2e', display:'flex', alignItems:'center', gap:8 }}><BarChart2 size={18} color="#1a56db" /> Stats Email</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {lastUpdate && <span style={{ fontSize:11, color:'#9ca3af' }}>{refreshing ? '↻ …' : `↻ ${lastUpdate.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}`}</span>}
            <button onClick={() => fetchStats(true)} title="Rafraîchir" style={{ border:'1px solid #e5e7eb', background:'white', borderRadius:8, padding:'4px 8px', cursor:'pointer', fontSize:13, color:'#6b7280' }}>↻</button>
            <button onClick={onClose} style={{ border:'none', background:'none', cursor:'pointer', color:'#6b7280', display:'flex', alignItems:'center' }}><X size={20} /></button>
          </div>
        </div>
        {loading && <div style={{ textAlign:'center', padding:'40px 0', color:'#9ca3af' }}><div style={{ display:'flex', justifyContent:'center' }}><BarChart2 size={32} color="#9ca3af" /></div><p style={{ marginTop:8 }}>Chargement...</p></div>}
        {error   && <div style={{ textAlign:'center', padding:'40px 0', color:'#dc2626' }}><div style={{ display:'flex', justifyContent:'center' }}><AlertTriangle size={32} color="#dc2626" /></div><p style={{ marginTop:8 }}>{error}</p></div>}
        {stats && !loading && (
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12, marginBottom:20 }}>
              {[
                { label:'Envoyés',  val:stats.total_sent, Icon:Upload,        color:'#1a56db', bg:'#eff6ff' },
                { label:'Délivrés', val:stats.delivered,  Icon:Check,         color:'#16a34a', bg:'#f0fdf4' },
                { label:'Ouverts',  val:stats.opened,     Icon:Eye,           color:'#7c3aed', bg:'#f5f3ff' },
                { label:'Cliqués',  val:stats.clicked,    Icon:MousePointer,  color:'#d97706', bg:'#fffbeb' },
                { label:'Bounces',  val:stats.bounced,    Icon:AlertTriangle, color:'#dc2626', bg:'#fef2f2' },
                { label:'Plaintes', val:stats.complained, Icon:Ban,           color:'#9f1239', bg:'#fff1f2' },
              ].map(s => (
                <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:'14px 10px', textAlign:'center' }}>
                  <div style={{ display:'flex', justifyContent:'center' }}><s.Icon size={22} color={s.color} /></div>
                  <div style={{ fontSize:24, fontWeight:700, color:s.color, marginTop:4 }}>{s.val}</div>
                  <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div style={{ background:'#eff6ff', borderRadius:12, padding:16, textAlign:'center' }}>
                <div style={{ fontSize:28, fontWeight:700, color:'#1a56db' }}>{stats.open_rate}%</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>Taux d'ouverture</div>
                <div style={{ marginTop:8, background:'#dbeafe', borderRadius:6, height:6 }}>
                  <div style={{ background:'#1a56db', borderRadius:6, height:6, width:`${Math.min(stats.open_rate,100)}%` }} />
                </div>
              </div>
              <div style={{ background:'#fffbeb', borderRadius:12, padding:16, textAlign:'center' }}>
                <div style={{ fontSize:28, fontWeight:700, color:'#d97706' }}>{stats.click_rate}%</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>Taux de clic</div>
                <div style={{ marginTop:8, background:'#fde68a', borderRadius:6, height:6 }}>
                  <div style={{ background:'#d97706', borderRadius:6, height:6, width:`${Math.min(stats.click_rate,100)}%` }} />
                </div>
              </div>
            </div>
            {stats.total_sent > 0 && stats.opened === 0 && stats.delivered === 0 && (
              <div style={{ background:'#fefce8', border:'1px solid #fde68a', borderRadius:10, padding:'12px 16px', fontSize:12, color:'#92400e', marginBottom:12 }}>
                💡 Les stats sont synchronisées en temps réel depuis Resend à chaque ouverture de cette fenêtre et toutes les 10 s.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MODAL NOTIFICATION PUSH ───────────────────────────────────────────────────
function ModalPush({ onClose, showToast }) {
  const [title, setTitle]       = useState('');
  const [body, setBody]         = useState('');
  const [employes, setEmployes] = useState([]);
  const [clients, setClients]   = useState([]);
  const [modeEmp, setModeEmp]   = useState('tous');   // 'aucun' | 'tous' | 'select'
  const [modeCli, setModeCli]   = useState('aucun');  // 'aucun' | 'tous' | 'select'
  const [selEmp, setSelEmp]     = useState([]);
  const [selCli, setSelCli]     = useState([]);
  const [sending, setSending]   = useState(false);
  const token = sessionStorage.getItem('qbdp_token');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetch(`${API}/api/employes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setEmployes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        // Uniquement les clients présents aujourd'hui
        const presents = (Array.isArray(d) ? d : []).filter(c => {
          if (!c.date_arrivee) return false;
          const arrivee = c.date_arrivee.split('T')[0];
          const depart  = c.date_depart ? c.date_depart.split('T')[0] : '9999-12-31';
          return arrivee <= today && today <= depart;
        });
        setClients(presents);
      }).catch(() => {});
  }, []);

  const toggleEmp = (id) => setSelEmp(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);
  const toggleCli = (id) => setSelCli(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);

  const resolvedEmpIds = modeEmp === 'tous' ? employes.map(e => e.id) : modeEmp === 'select' ? selEmp : [];
  const resolvedCliIds = modeCli === 'tous' ? clients.map(c => c.id)  : modeCli === 'select' ? selCli  : [];
  const totalDest = resolvedEmpIds.length + resolvedCliIds.length;
  const resolvedEmps = modeEmp === 'tous' ? employes : modeEmp === 'select' ? employes.filter(e => selEmp.includes(e.id)) : [];
  const resolvedClis = modeCli === 'tous' ? clients  : modeCli === 'select' ? clients.filter(c => selCli.includes(c.id))  : [];
  const nbWithToken  = resolvedEmps.filter(e => e.push_token).length + resolvedClis.filter(c => c.push_token).length;

  const handleSend = async () => {
    if (!title || !body) { showToast('Titre et message requis', false); return; }
    if (totalDest === 0) { showToast('Aucun destinataire sélectionné', false); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/push/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, body, employe_ids: resolvedEmpIds, client_ids: resolvedCliIds }),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); setSending(false); return; }
      showToast(`Notification envoyée (${d.sent} appareil${d.sent !== 1 ? 's' : ''})`);
      onClose();
    } catch { showToast('Erreur réseau', false); }
    setSending(false);
  };

  const ModeChips = ({ mode, setMode, options }) => (
    <div style={{ display:'flex', gap:6, marginBottom:10 }}>
      {options.map(o => (
        <div key={o.id} onClick={() => setMode(o.id)}
          style={{ ...S.chip(mode === o.id), fontSize:12, padding:'6px 12px' }}>{o.label}</div>
      ))}
    </div>
  );

  const PersonList = ({ list, selIds, toggle, emptyLabel }) => (
    <div style={{ maxHeight:180, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:10, marginBottom:4 }}>
      {list.length === 0
        ? <div style={{ padding:16, textAlign:'center', color:'#9ca3af', fontSize:13 }}>{emptyLabel}</div>
        : list.map((item, i) => {
          const sel = selIds.includes(item.id);
          return (
            <div key={item.id} onClick={() => toggle(item.id)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', cursor:'pointer',
                background:sel?'#eff6ff':i%2===0?'#fff':'#fafafa',
                borderBottom:i < list.length-1?'1px solid #f3f4f6':'none' }}>
              <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?'#1a56db':'#d1d5db'}`,
                background:sel?'#1a56db':'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {sel && <Check size={10} color="#fff" />}
              </div>
              <div style={{ flex:1, fontSize:13, fontWeight:500, color:'#1a1a2e' }}>{item.nom}</div>
              {item.numero_chambre && <div style={{ fontSize:11, color:'#9ca3af' }}>Ch. {item.numero_chambre}</div>}
              <div style={{ fontSize:11, color: item.push_token ? '#16a34a' : '#d1d5db', display:'flex', alignItems:'center' }}>
                {item.push_token ? <Smartphone size={12} /> : '—'}
              </div>
            </div>
          );
        })}
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:20, width:520, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ padding:'22px 28px 14px', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:17, color:'#1a1a2e', display:'flex', alignItems:'center', gap:8 }}>
            <Bell size={17} color="#1a56db" /> Notification push
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', display:'flex' }}><X size={20} /></button>
        </div>

        <div style={{ padding:'20px 28px', display:'flex', flexDirection:'column', gap:14 }}>
          {/* Titre + Message */}
          <div>
            <label style={S.label}>Titre</label>
            <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Fermeture exceptionnelle" maxLength={50} autoFocus />
          </div>
          <div>
            <label style={S.label}>Message</label>
            <textarea style={{ ...S.textarea, minHeight:72 }} value={body} onChange={e => setBody(e.target.value)} placeholder="Votre message..." maxLength={200} />
          </div>

          {/* Section Employés */}
          <div style={{ background:'#f9fafb', borderRadius:12, padding:'14px 16px', border:'1px solid #e5e7eb' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1a1a2e', display:'flex', alignItems:'center', gap:6 }}>
                <User size={13} /> Employés <span style={{ fontSize:11, color:'#9ca3af', fontWeight:400 }}>({employes.length})</span>
              </div>
              {modeEmp !== 'aucun' && <span style={{ fontSize:11, color:'#1a56db', fontWeight:600 }}>{resolvedEmpIds.length} sélectionné{resolvedEmpIds.length > 1 ? 's' : ''}</span>}
            </div>
            <ModeChips mode={modeEmp} setMode={setModeEmp}
              options={[{ id:'aucun', label:'Aucun' }, { id:'tous', label:'Tous' }, { id:'select', label:'Choisir' }]} />
            {modeEmp === 'select' && <PersonList list={employes} selIds={selEmp} toggle={toggleEmp} emptyLabel="Aucun employé" />}
          </div>

          {/* Section Clients présents */}
          <div style={{ background:'#f9fafb', borderRadius:12, padding:'14px 16px', border:'1px solid #e5e7eb' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1a1a2e', display:'flex', alignItems:'center', gap:6 }}>
                <Building2 size={13} /> Clients présents <span style={{ fontSize:11, color:'#9ca3af', fontWeight:400 }}>({clients.length} aujourd'hui)</span>
              </div>
              {modeCli !== 'aucun' && <span style={{ fontSize:11, color:'#1a56db', fontWeight:600 }}>{resolvedCliIds.length} sélectionné{resolvedCliIds.length > 1 ? 's' : ''}</span>}
            </div>
            <ModeChips mode={modeCli} setMode={setModeCli}
              options={[{ id:'aucun', label:'Aucun' }, { id:'tous', label:'Tous présents' }, { id:'select', label:'Choisir' }]} />
            {modeCli === 'select' && <PersonList list={clients} selIds={selCli} toggle={toggleCli} emptyLabel="Aucun client présent aujourd'hui" />}
          </div>

          {/* Résumé */}
          {totalDest > 0 && (
            <div style={{ background: nbWithToken > 0 ? '#eff6ff' : '#fef9c3', borderRadius:10, padding:'10px 14px', fontSize:13, color: nbWithToken > 0 ? '#1a56db' : '#b45309', fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
              <Bell size={13} />
              {nbWithToken > 0
                ? <>{nbWithToken} appareil{nbWithToken > 1 ? 's' : ''} · {resolvedEmpIds.length} employé{resolvedEmpIds.length > 1 ? 's' : ''} + {resolvedCliIds.length} client{resolvedCliIds.length > 1 ? 's' : ''}</>
                : <>Aucun appareil connecté parmi les {totalDest} destinataire{totalDest > 1 ? 's' : ''} (app non installée)</>
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 28px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', gap:10 }}>
          <button onClick={onClose} style={S.btn(false)}>Annuler</button>
          <button onClick={handleSend}
            style={{ ...S.btn(true), opacity: (sending || !title || !body || nbWithToken === 0) ? 0.45 : 1, cursor: (sending || !title || !body || nbWithToken === 0) ? 'not-allowed' : 'pointer' }}
            disabled={sending || !title || !body || nbWithToken === 0}>
            {sending ? 'Envoi...' : <><Bell size={14} /> Envoyer ({nbWithToken})</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── COMMUNICATIONS (principal) ─────────────────────────────────────────────────
export default function Communications() {
  const [campaigns, setCampaigns]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showNouvelle, setShowNouvelle] = useState(false);
  const [showLancer, setShowLancer]     = useState(false);
  const [showModifier, setShowModifier] = useState(null);
  const [showStats, setShowStats]       = useState(null);
  const [showPush, setShowPush]         = useState(false);
  const [sending, setSending]           = useState(null);
  const [toast, setToast]               = useState(null);
  const { showConfirm, confirmModal } = useConfirm();
  const token = sessionStorage.getItem('qbdp_token');

  const showToast = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setCampaigns(Array.isArray(d) ? d : []);
    } catch { setCampaigns([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSendNow = async (id) => {
    setSending(id);
    try {
      const r = await fetch(`${API}/api/campaigns/${id}/send`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) showToast(d.error || 'Erreur envoi', false);
      else showToast(`✅ ${d.sent} message(s) envoyé(s) !`);
      load();
    } catch { showToast('Erreur envoi', false); }
    setSending(null);
  };

  const handleAnnulerPlanification = async (id) => {
    try {
      const r = await fetch(`${API}/api/campaigns/${id}/schedule`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      if (!r.ok) { const d = await r.json(); showToast(d.error || 'Erreur', false); return; }
      showToast('Planification annulée');
      load();
    } catch { showToast('Erreur réseau', false); }
  };

  const handleDelete = async (id, nom) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CETTE CAMPAGNE',
      subtitle: nom,
      body: 'Cette action est irréversible. La campagne et toutes ses statistiques seront définitivement supprimées.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    try {
      await fetch(`${API}/api/campaigns/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
      load();
    } catch { showToast('Erreur réseau', false); }
  };

  // "Planifier" button on a draft: if scheduled_at exists → POST schedule; else → open Modifier on planification tab
  const handlePlanifier = async (c) => {
    if (!c.scheduled_at) {
      showToast('Définissez d\'abord une date dans l\'onglet Planification', true);
      setShowModifier({ ...c, defaultTab: 'planification' });
    } else {
      try {
        const r = await fetch(`${API}/api/campaigns/${c.id}/schedule`, { method:'POST', headers:{ Authorization:`Bearer ${token}` } });
        const d = await r.json();
        if (!r.ok) showToast(d.error || 'Erreur', false);
        else { showToast('Campagne planifiée ✓'); load(); }
      } catch { showToast('Erreur réseau', false); }
    }
  };

  const handleAnnivClick = (periode, liste) => {
    setShowLancer(true);
  };

  // Badge for scheduled with date display
  const scheduledBadge = (c) => {
    if (c.status !== 'scheduled' || !c.scheduled_at) return null;
    const dt = new Date(c.scheduled_at).toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
    return (
      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, background:'#fffbeb', color:'#d97706', display:'inline-flex', alignItems:'center', gap:4 }}>
        <Calendar size={11} /> {dt}
      </span>
    );
  };

  return (
    <div style={{ padding:32, maxWidth:1100, margin:'0 auto', paddingBottom:100 }}>

      {toast && (
        <div style={{ position:'fixed', top:24, right:24, zIndex:3000, padding:'12px 20px', borderRadius:12,
          background:toast.ok?'#f0fdf4':'#fef2f2', color:toast.ok?'#16a34a':'#dc2626',
          border:`1px solid ${toast.ok?'#bbf7d0':'#fecaca'}`, fontWeight:600, fontSize:13,
          boxShadow:'0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {showNouvelle && (
        <WizardNouvelle onClose={() => setShowNouvelle(false)} onSaved={load} showToast={showToast} />
      )}
      {showLancer && (
        <WizardLancer
          onClose={() => setShowLancer(false)}
          onSaved={load} showToast={showToast} campaigns={campaigns} />
      )}
      {showModifier && (
        <WizardModifier
          campaign={showModifier}
          defaultTab={showModifier.defaultTab}
          onClose={() => setShowModifier(null)}
          onSaved={() => { setShowModifier(null); load(); }}
          showToast={showToast} />
      )}
      {showStats && <ModalStats campaignId={showStats} onClose={() => setShowStats(null)} />}
      {showPush  && <ModalPush onClose={() => setShowPush(false)} showToast={showToast} />}

      {/* Page header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:'#1a1a2e', margin:0, display:'flex', alignItems:'center', gap:10 }}><MessageSquare size={22} color="#1a56db" /> Communications</h1>
          <p style={{ fontSize:13, color:'#6b7280', marginTop:4 }}>Gérez vos campagnes email et SMS</p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
          {[
            { label:'Nouvelle campagne',    Icon:Sparkles, bg:'#1e3a8a', shadow:'rgba(30,58,138,0.35)',  onClick:() => setShowNouvelle(true) },
            { label:'Lancer une campagne',  Icon:Send,     bg:'#5b8dd9', shadow:'rgba(91,141,217,0.35)', onClick:() => setShowLancer(true)  },
            { label:'Notification push',    Icon:Bell,     bg:'#0d9488', shadow:'rgba(13,148,136,0.35)', onClick:() => setShowPush(true)    },
          ].map(b => (
            <button key={b.label} onClick={b.onClick}
              style={{ width:200, padding:'10px 0', borderRadius:12, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
                background:b.bg, color:'#fff', textAlign:'center', boxShadow:`0 4px 16px ${b.shadow}`, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <b.Icon size={14} /> {b.label}
            </button>
          ))}
        </div>
      </div>

      <WidgetAnniversaires onLancer={handleAnnivClick} />

      {/* Stats bar */}
      {campaigns.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:24 }}>
          {[
            { label:'Total',      val:campaigns.length,                                  Icon:Megaphone,  color:'#1a56db' },
            { label:'Envoyées',   val:campaigns.filter(c=>c.status==='sent').length,      Icon:Check,      color:'#16a34a' },
            { label:'Planifiées', val:campaigns.filter(c=>c.status==='scheduled').length, Icon:Calendar,   color:'#d97706' },
            { label:'Brouillons', val:campaigns.filter(c=>c.status==='draft').length,     Icon:FileText,   color:'#6b7280' },
          ].map(stat => (
            <div key={stat.label} style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', padding:'16px 20px' }}>
              <div><stat.Icon size={20} color={stat.color} /></div>
              <div style={{ fontSize:24, fontWeight:700, color:stat.color, marginTop:4 }}>{stat.val}</div>
              <div style={{ fontSize:12, color:'#6b7280' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Campaign list */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#9ca3af' }}>
          <div style={{ display:'flex', justifyContent:'center' }}><MessageSquare size={32} color="#9ca3af" /></div><p>Chargement...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#9ca3af' }}>
          <p style={{ fontWeight:600, color:'#374151', marginTop:12 }}>Aucune campagne</p>
          <p style={{ fontSize:13, marginTop:4 }}>Créez votre première campagne pour communiquer avec vos clients et employés.</p>
        </div>
      ) : (
        <div>
          {campaigns.map(c => {
            const tplInfo  = TEMPLATES.find(t => t.id === c.template) || TEMPLATES[4];
            const typeInfo = TYPES.find(x => x.id === c.type);
            return (
              <div key={c.id} style={{ background:'#fff', borderRadius:14, border:'1px solid #e5e7eb', padding:20, marginBottom:12, display:'flex', alignItems:'center', gap:16 }}>
                {/* Icon */}
                <div style={{ width:44, height:44, borderRadius:12, background:`linear-gradient(135deg,${tplInfo.color},${tplInfo.accent})`,
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <tplInfo.Icon size={22} color="white" />
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:14, color:'#1a1a2e' }}>{c.name}</span>
                    {badge(c.status)}
                    {scheduledBadge(c)}
                    {c.attachment_name && <span style={{ fontSize:11, color:'#6b7280', display:'flex', alignItems:'center', gap:3 }}><Paperclip size={11} />{c.attachment_name}</span>}
                  </div>
                  <div style={{ fontSize:12, color:'#6b7280', display:'flex', gap:12, flexWrap:'wrap' }}>
                    {typeInfo && <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}><typeInfo.Icon size={11} />{typeInfo.Icon2 && <typeInfo.Icon2 size={11} />} {typeInfo.label}</span>}
                    {c.recipient_ids && c.recipient_ids.length > 0
                      ? <span style={{ display:'flex', alignItems:'center', gap:3 }}><Target size={11} />{c.recipient_ids.length} ciblé{c.recipient_ids.length > 1 ? 's' : ''}</span>
                      : c.cible && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Target size={11} />{c.cible === 'clients' ? 'Tous les clients' : c.cible === 'employes' ? 'Tous les employés' : 'Clients + Employés'}</span>
                    }
                    {c.total_recipients > 0 && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Upload size={11} />{c.total_recipients} envoyé{c.total_recipients > 1 ? 's' : ''}</span>}
                    {c.sent_at && <span>• {new Date(c.sent_at).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:8, flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>

                  {/* draft buttons */}
                  {c.status === 'draft' && <>
                    <button onClick={() => setShowModifier(c)} title="Modifier"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid var(--border)', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><Pencil size={13} color="#374151" /></button>
                    <button onClick={() => handlePlanifier(c)} title="Planifier"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #bfdbfe', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><Calendar size={13} color="#1a56db" /></button>
                    <button onClick={() => handleSendNow(c.id)} disabled={sending===c.id} title="Envoyer maintenant"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #bfdbfe', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><Send size={13} color="#1e3a8a" /></button>
                  </>}

                  {/* scheduled buttons */}
                  {c.status === 'scheduled' && <>
                    <button onClick={() => setShowModifier(c)} title="Modifier"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid var(--border)', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><Pencil size={13} color="#374151" /></button>
                    <button onClick={() => handleSendNow(c.id)} disabled={sending===c.id} title="Envoyer maintenant"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #bfdbfe', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><Send size={13} color="#1e3a8a" /></button>
                    <button onClick={() => handleAnnulerPlanification(c.id)} title="Annuler planification"
                      style={{ padding:'5px 8px', borderRadius:6, border:'1px solid #fee2e2', background:'white', cursor:'pointer', display:'flex', alignItems:'center' }}><X size={13} color="#dc2626" /></button>
                  </>}

                  {/* sent/sending buttons */}
                  {(c.status === 'sent' || c.status === 'sending') && (
                    <button onClick={() => setShowStats(c.id)}
                      style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #dbeafe', cursor:'pointer', background:'#eff6ff', color:'#1a56db', fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:5 }}>
                      <BarChart2 size={13} /> Stats
                    </button>
                  )}

                  {/* delete always visible */}
                  <button onClick={() => handleDelete(c.id, c.nom)}
                    style={{ padding:'7px 10px', borderRadius:8, border:'1px solid #fee2e2', cursor:'pointer', background:'#fff', color:'#dc2626', display:'flex', alignItems:'center' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirmModal}
    </div>
  );
}
